import React, { useState, useEffect, useRef } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Modal,
  Row,
  Col,
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  writeBatch,
  doc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { FaTrash, FaEye, FaHistory, FaSearch, FaPlus, FaMinus, FaCheck, FaClipboardCheck, FaUserAlt, FaBoxOpen, FaIdCard, FaPenNib, FaFilePdf, FaUndo } from "react-icons/fa";
import { BiUser, BiIdCard, BiPackage, BiHistory, BiSearch, BiCheckCircle, BiDevices, BiWrench, BiFile } from "react-icons/bi";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

function EntregaActivos({ usuario }) {
  const ruc = usuario?.ruc;
  const rucStr = ruc?.toString();
  const { id } = useParams();
  const location = useLocation();
  const [empleadoLocal, setEmpleadoLocal] = useState(location.state?.usuario);
  const [eppsAsignados, setEppsAsignados] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [catalogoEpps, setCatalogoEpps] = useState([]);
  const [activeTab, setActiveTab] = useState("epps");
  const [busqueda, setBusqueda] = useState("");
  const [busquedaHistorial, setBusquedaHistorial] = useState("");
  const [requestsMap, setRequestsMap] = useState(new Map());
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (showModal && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 300);
    }
  }, [showModal]);

  useEffect(() => {
    if (id && rucStr && empleadoLocal) {
      cargarEppsAsignados();
      cargarCatalogoEpps();
    }
  }, [id, rucStr, empleadoLocal, activeTab]);

  const [loading, setLoading] = useState(false);
  const [eppsSeleccionados, setEppsSeleccionados] = useState([]);

  // Estados para el nuevo EPP a asignar
  const [eppSeleccionado, setEppSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [observaciones, setObservaciones] = useState("");


  const cargarEppsAsignados = async () => {
    try {
      const workerDni = empleadoLocal?.dni?.toString();
      if (!workerDni || !rucStr) return;

      const workerDniStr = workerDni;
      const workerDniNum = parseInt(workerDni);

      const qReqStr = query(collection(db, "epp_requests"), where("worker_id", "==", workerDniStr));
      const qReqNum = query(collection(db, "epp_requests"), where("worker_id", "==", workerDniNum));
      const qConstStr = query(collection(db, "epp_constancias"), where("worker_id", "==", workerDniStr));
      const qConstNum = query(collection(db, "epp_constancias"), where("worker_id", "==", workerDniNum));

      const [snapReqStr, snapReqNum, snapConstStr, snapConstNum] = await Promise.all([
        getDocs(qReqStr), getDocs(qReqNum),
        getDocs(qConstStr), getDocs(qConstNum)
      ]);

      const constanciasList = [];
      const processConst = (snap) => {
        snap.forEach(doc => {
          const d = doc.data();
          if (d.ruc?.toString() === rucStr) {
            constanciasList.push({ id: doc.id, ...d });
          }
        });
      };
      processConst(snapConstStr);
      processConst(snapConstNum);

      const constanciasMap = {};
      const deliveryIds = new Set();
      constanciasList.forEach(c => {
        constanciasMap[c.delivery_id] = c.pdf_url;
        if (c.delivery_id) deliveryIds.add(c.delivery_id);
      });

      const reqMap = new Map();
      const processReqSnap = (snap) => {
        snap.forEach(doc => reqMap.set(doc.id, { id: doc.id, ...doc.data() }));
      };
      processReqSnap(snapReqStr);
      processReqSnap(snapReqNum);

      for (const reqId of deliveryIds) {
        if (!reqMap.has(reqId)) {
          try {
            const docRef = doc(db, "epp_requests", reqId);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
              reqMap.set(reqId, { id: reqId, ...snap.data() });
            }
          } catch (e) {
            // Error silencioso para limpieza de logs
          }
        }
      }

      setRequestsMap(reqMap);

      const epps = [];
      reqMap.forEach((data, docId) => {
        const docRuc = (data.ruc_empresa || data.ruc)?.toString();
        if (docRuc === rucStr) {
          data.items.forEach((item) => {
            epps.push({
              id: docId,
              eppId: item.id,
              nombreEpp: item.name,
              cantidad: item.quantity,
              fechaEntrega: data.completed_at,
              entregadoPor: data.responsible_id,
              talla: item.size,
              observaciones: item.observations || "",
              status: data.status,
              firmaUrl: data.firma_url,
              pdfUrl: constanciasMap[docId] || null
            });
          });
        }
      });
      setEppsAsignados(epps.sort((a, b) => {
        const dateA = a.fechaEntrega?.seconds || 0;
        const dateB = b.fechaEntrega?.seconds || 0;
        return dateB - dateA;
      }));
    } catch (error) {
      console.error("Error al cargar EPPs asignados:", error);
    }
  };

  const cargarCatalogoEpps = async () => {
    try {
      setLoading(true);
      let collectionName = "epp_catalogo";
      if (activeTab === "equipos") collectionName = "equipos_catalogo";
      if (activeTab === "herramientas") collectionName = "herramientas_catalogo";

      const q = query(collection(db, collectionName), where("ruc_empresa", "==", rucStr));
      const querySnapshot = await getDocs(q);
      const epps = [];
      querySnapshot.forEach((doc) => {
        const d = doc.data();
        // Normalizar campos para que la UI los entienda (stock vs estado)
        epps.push({
          id: doc.id,
          ...d,
          // Para equipos/herramientas, el stock es 1 si está Disponible
          stock: d.stock !== undefined ? d.stock : (d.estado === "Disponible" ? 1 : 0)
        });
      });
      setCatalogoEpps(epps);
    } catch (error) {
      console.error("Error al cargar catálogo:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAsignarEpp = async () => {
    if (!empleadoLocal?.dni || eppsSeleccionados.length === 0) {
      Swal.fire({ title: "Advertencia", text: "Debe seleccionar al menos un EPP", icon: "warning" });
      return;
    }

    try {
      setLoading(true);

      // Verificar stock en las colecciones correspondientes
      for (const epp of eppsSeleccionados) {
        let collect = "epp_catalogo";
        if (epp.type === "equipo") collect = "equipos_catalogo";
        if (epp.type === "herramienta") collect = "herramientas_catalogo";

        const eppRef = doc(db, collect, epp.id);
        const eppSnap = await getDoc(eppRef);
        if (!eppSnap.exists()) throw new Error(`El artículo ${epp.nombre} no existe en ${collect}.`);

        const eppData = eppSnap.data();
        if (eppData.stock !== undefined && eppData.stock < epp.cantidadAsignada) {
          throw new Error(`Stock insuficiente para ${epp.nombre}. Disponible: ${eppData.stock}`);
        }

        // Para equipos que no tienen stock numérico sino estado 'Disponible'
        if (epp.type === "equipo" && eppData.estado !== "Disponible") {
          throw new Error(`El equipo ${epp.nombre} no se encuentra disponible actualmente.`);
        }
      }

      // 1. Obtener Firma del Responsable (el que asigna en la web)
      let firmaResponsable = "";
      try {
        const qFirma = query(
          collection(db, "firmas"),
          where("dni", "==", usuario?.dni || ""),
          where("ruc", "==", rucStr),
          where("activo", "==", true)
        );
        const snapFirma = await getDocs(qFirma);
        if (!snapFirma.empty) {
          firmaResponsable = snapFirma.docs[0].data().firmaUrl || "";
        }
      } catch (e) {
        console.error("Error al obtener firma del responsable:", e);
      }

      // 2. Crear documento en epp_requests con estado 'pending'
      const docRef = await addDoc(collection(db, "epp_requests"), {
        created_at: serverTimestamp(),
        completed_at: null, // No se completa hasta que firma el trabajador
        responsible_id: usuario?.dni || "admin",
        responsible_signature_url: firmaResponsable, // NUEVO: Firma del que asigna
        worker_id: empleadoLocal.dni,
        ruc_empresa: rucStr,
        status: "pending",
        firma_url: "", // Reservado para la firma del trabajador
        items: eppsSeleccionados.map((epp) => ({
          id: epp.codigo.toString(),
          name: epp.nombre,
          quantity: parseInt(epp.cantidadAsignada),
          size: epp.talla || "Standard",
          observations: epp.observacionesEpp || "",
          itemType: epp.type || "epp",
          item_doc_id: epp.id,
        })),
      });

      const deliveryId = docRef.id;
      await updateDoc(docRef, { id: deliveryId });

      // 2. Vincular el ID de solicitud al ítem original para seguimiento (Solo para Equipos y Herramientas)
      for (const epp of eppsSeleccionados) {
        if (epp.type !== "epp") {
          let collect = epp.type === "equipo" ? "equipos_catalogo" : "herramientas_catalogo";
          const originalRef = doc(db, collect, epp.id);
          await updateDoc(originalRef, {
            current_request_id: deliveryId,
            // Opcional: guardamos datos preventivos, aunque el estado sigue siendo 'Disponible' hasta la firma
            trabajadorDni: empleadoLocal.dni,
            trabajadorNombre: `${empleadoLocal.name} ${empleadoLocal.apellidos}`,
            fechaReserva: serverTimestamp()
          });
        }
      }

      await cargarEppsAsignados();
      setShowModal(false);
      setEppsSeleccionados([]);

      // AUDITORÍA ASIGNACIÓN EPP
      try {
        await logBusinessAction("ASIGNACION_EPP", "GestionEPP", {
          dniTrabajador: empleadoLocal.dni,
          colaborador: `${empleadoLocal.name} ${empleadoLocal.apellidos}`,
          articulos: eppsSeleccionados.map(e => ({ nombre: e.nombre, cantidad: e.cantidadAsignada, tipo: e.type || "epp" }))
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de asignación de EPP", e);
      }

      Swal.fire({
        title: "¡Reserva Exitosa!",
        text: "La entrega ha sido registrada como 'Reservada'. El trabajador debe firmar en su App para formalizar la entrega y descontar el stock.",
        icon: "success"
      });
    } catch (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleProcesarDevolucion = async (eppRequest) => {
    const { value: reason } = await Swal.fire({
      title: "¿Procesar Devolución?",
      input: "select",
      inputOptions: {
        "Talla incorrecta": "Talla incorrecta",
        "Defecto de fábrica": "Defecto de fábrica",
        "No lo recibió": "No lo recibió",
        "Otros": "Otros",
      },
      inputPlaceholder: "Selecciona el motivo",
      showCancelButton: true,
      confirmButtonText: "Confirmar Devolución",
      confirmButtonColor: "#d33",
      text: "Esto devolverá el stock al inventario y anulará la constancia.",
      inputValidator: (value) => {
        if (!value) return "Debes seleccionar un motivo";
      }
    });

    if (reason) {
      try {
        setLoading(true);
        // 1. Revertir Stock y Kardex (Solo si estaba completed)
        if (eppRequest.status === "completed") {
          for (const item of eppRequest.items) {
            // Buscar por código el EPP en el catálogo actual del RUC
            const q = query(
              collection(db, "epp_catalogo"),
              where("ruc_empresa", "==", rucStr),
              where("codigo", "==", parseInt(item.id))
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
              const eppDoc = snap.docs[0];
              const eppRef = doc(db, "epp_catalogo", eppDoc.id);
              const eppData = eppDoc.data();

              // Actualizar Stock
              await updateDoc(eppRef, { stock: (eppData.stock || 0) + item.quantity });

              // Registrar en Kardex
              await addDoc(collection(db, "epp_kardex"), {
                eppId: eppDoc.id,
                codigo: item.id,
                nombre: item.name,
                tipo: "ingreso",
                cantidad: item.quantity,
                observaciones: `DEVOLUCIÓN SOLICITUD ${eppRequest.id} - MOTIVO: ${reason}`,
                trabajador: `${empleadoLocal?.name || ""} ${empleadoLocal?.apellidos || ""}`,
                dniTrabajador: empleadoLocal?.dni,
                ruc_empresa: rucStr,
                fecha: new Date(),
                referencia_solicitud: eppRequest.id
              });
            }
          }
        }

        // 2. Marcar como devuelto
        const reqRef = doc(db, "epp_requests", eppRequest.id);
        await updateDoc(reqRef, {
          status: "returned",
          motivo_devolucion: reason,
          fecha_devolucion: serverTimestamp(),
          responsable_devolucion: usuario?.dni || "admin"
        });

        Swal.fire("Devuelto", "El stock ha sido repuesto y la solicitud marcada como devuelta.", "success");

        // AUDITORÍA DEVOLUCIÓN EPP
        try {
          await logBusinessAction("DEVOLUCION_EPP", "GestionEPP", {
            dniTrabajador: empleadoLocal?.dni,
            colaborador: `${empleadoLocal?.name || ""} ${empleadoLocal?.apellidos || ""}`,
            motivo: reason,
            articulos: eppRequest.items.map(i => ({ nombre: i.name, cantidad: i.quantity, tipo: i.itemType || "epp" }))
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de devolución de EPP", e);
        }

        cargarEppsAsignados();
      } catch (error) {
        console.error("Error en devolución:", error);
        Swal.fire("Error", "No se pudo procesar la devolución.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const agregarEpp = async (eppParam = null, qtyParam = 1, obsParam = "") => {
    const itemToAdd = eppParam || eppSeleccionado;
    if (!itemToAdd) return;

    // Detectar tipo según el catálogo actual
    const type = activeTab === "epps" ? "epp" : (activeTab === "equipos" ? "equipo" : "herramienta");

    const yaSeleccionado = eppsSeleccionados.find((e) => e.id === itemToAdd.id);
    if (yaSeleccionado) {
      Swal.fire({ title: "Advertencia", text: "Este artículo ya está en el carrito", icon: "warning" });
      return;
    }

    // --- SOLO PARA EPPs: Validar periodo de reposición ---
    if (type === "epp" && itemToAdd.periodoReposicion) {
      const ultimaEntrega = eppsAsignados.find(h =>
        h.eppId.toString() === itemToAdd.codigo.toString() &&
        h.status === "completed"
      );

      if (ultimaEntrega) {
        const fechaUltima = ultimaEntrega.fechaEntrega?.toDate ? ultimaEntrega.fechaEntrega.toDate() : new Date(ultimaEntrega.fechaEntrega);
        const hoy = new Date();
        const diffMonths = Math.floor(Math.abs(hoy - fechaUltima) / (1000 * 60 * 60 * 24 * 30.44));

        if (diffMonths < itemToAdd.periodoReposicion) {
          const result = await Swal.fire({
            title: "¡Alerta de Reposición!",
            html: `Este colaborador recibió <strong>${itemToAdd.nombre}</strong> hace <strong>${diffMonths} meses</strong> (Periodo: ${itemToAdd.periodoReposicion} meses).<br/><br/>¿Desea proceder?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f97316",
            confirmButtonText: "Sí, entregar",
            cancelButtonText: "Cancelar"
          });
          if (!result.isConfirmed) return;
        }
      }
    }

    setEppsSeleccionados([...eppsSeleccionados, {
      ...itemToAdd,
      cantidadAsignada: qtyParam,
      observacionesEpp: obsParam,
      type: type
    }]);
    setEppSeleccionado(null);
    setCantidad(1);
    setObservaciones("");
  };

  const actualizarCantidadDirecta = (index, nuevaCant) => {
    const lista = [...eppsSeleccionados];
    const item = lista[index];
    if (nuevaCant < 1) return;
    if (nuevaCant > item.stock) {
      Swal.fire("Aviso", "No hay suficiente stock disponible", "warning");
      return;
    }
    lista[index] = { ...item, cantidadAsignada: parseInt(nuevaCant) };
    setEppsSeleccionados(lista);
  };

  const actualizarObsDirecta = (index, nuevaObs) => {
    const lista = [...eppsSeleccionados];
    lista[index] = { ...lista[index], observacionesEpp: nuevaObs };
    setEppsSeleccionados(lista);
  };

  const removerEpp = (index) => {
    setEppsSeleccionados(eppsSeleccionados.filter((_, i) => i !== index));
  };

  const eppsFiltrados = catalogoEpps.filter(
    (epp) =>
      epp.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      epp.codigo.toString().includes(busqueda)
  );

  const historialFiltrado = eppsAsignados.filter((epp) =>
    epp.nombreEpp.toLowerCase().includes(busquedaHistorial.toLowerCase()) ||
    epp.eppId.toString().includes(busquedaHistorial)
  );

  return (
    <div className="min-vh-100 py-4" style={{ backgroundColor: "#f8f9fa" }}>
      <Container fluid className="px-lg-5 px-3">
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75" style={{ zIndex: 1080 }}>
            <div className="text-center">
              <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
              <p className="text-white mt-3">Procesando...</p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-0 mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              borderBottom: "3px solid #ea580c",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-white text-orange d-flex align-items-center justify-content-center shadow-sm"
                    style={{ width: "60px", height: "60px", color: "#f97316" }}
                  >
                    <FaUserAlt size={30} />
                  </div>
                  <div>
                    <h2 className="fw-bold mb-0">{empleadoLocal?.name} {empleadoLocal?.apellidos}</h2>
                    <div className="d-flex gap-3 opacity-90 small">
                      <span><FaIdCard className="me-1" /> DNI: {empleadoLocal?.dni}</span>
                      <span><FaBoxOpen className="me-1" /> Entrega de Activos y Equipos</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="light" size="lg" onClick={() => setShowModal(true)}
                  className="fw-bold shadow-sm d-flex align-items-center gap-2 ms-auto"
                  style={{ borderRadius: "12px", color: "#ea580c" }}>
                  <FaClipboardCheck size={20} />
                  Nueva Entrega
                </Button>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div>
                <h5 className="fw-bold mb-1 text-dark">
                  <BiHistory className="me-2 text-orange" size={24} />
                  Historial de Dotación
                </h5>
                <p className="text-muted small mb-0">Registro histórico de equipos y EPPs entregados al colaborador</p>
              </div>
              <Badge bg="orange" className="p-2 px-3 text-white" style={{ backgroundColor: "#f97316", borderRadius: "10px" }}>
                {eppsAsignados.length} Entregas Registradas
              </Badge>
            </div>

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <div className="position-relative">
                  <BiSearch
                    className="position-absolute"
                    style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d", fontSize: "1.2rem" }}
                  />
                  <Form.Control
                    type="text"
                    placeholder="Buscar en el historial por nombre o código de artículo..."
                    value={busquedaHistorial}
                    onChange={(e) => setBusquedaHistorial(e.target.value)}
                    className="ps-5 form-control-lg border-0 shadow-none bg-light"
                    style={{ borderRadius: "10px" }}
                  />
                </div>
              </Card.Body>
            </Card>

            <div className="table-responsive shadow-sm" style={{ borderRadius: "12px" }}>
              <Table hover className="align-middle mb-0 bg-white">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3 ps-4">Código</th>
                    <th className="py-3">Artículo</th>
                    <th className="py-3">Cantidad</th>
                    <th className="py-3">Talla</th>
                    <th className="py-3">Fecha Entrega</th>
                    <th className="py-3 text-center">Estado</th>
                    <th className="py-3 text-center pe-4">Documentos</th>
                  </tr>
                </thead>
                <tbody>
                  {historialFiltrado.length === 0 ? (
                    <tr><td colSpan="6" className="text-center py-5 text-muted">No hay registros de entrega.</td></tr>
                  ) : (
                    historialFiltrado.map((epp, index) => (
                      <tr key={`${epp.id}-${index}`}>
                        <td className="py-3 px-4 fw-bold"><code>{epp.eppId}</code></td>
                        <td className="py-3 px-3">
                          <div className="fw-bold text-dark">{epp.nombreEpp}</div>
                          <small className="text-muted">{epp.observaciones || "Entrega regular"}</small>
                        </td>
                        <td className="py-3 px-3"><Badge bg="light" text="dark" className="border">{epp.cantidad} Unid.</Badge></td>
                        <td className="py-3 px-3">
                          <Badge bg="secondary" className="fw-normal">{epp.talla}</Badge>
                        </td>
                        <td className="py-3 px-3">
                          <div className="small text-dark fw-semibold">
                            {epp.fechaEntrega?.toDate ? epp.fechaEntrega.toDate().toLocaleDateString() : "-"}
                          </div>
                          <small className="text-muted">Fecha de entrega</small>
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Badge
                            bg={
                              epp.status === "pending" || epp.status === "reserved" ? "warning" :
                                epp.status === "returned" ? "danger" : "success"
                            }
                            pill
                            className="px-3 py-2 text-uppercase"
                            style={{ fontSize: "0.7rem", fontWeight: "800", letterSpacing: "0.5px" }}
                          >
                            {epp.status === "pending" || epp.status === "reserved" ? "Reservado" :
                              epp.status === "returned" ? "Devuelto" : "Entregado"}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 text-center pe-4">
                          <div className="d-flex justify-content-center gap-2">
                            {epp.pdfUrl && epp.status === "completed" && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => window.open(epp.pdfUrl, "_blank")}
                                title="Ver Constancia PDF"
                              >
                                <FaFilePdf />
                              </Button>
                            )}
                            {epp.status === "completed" && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                  // Reconstruir objeto request para la función
                                  const fullReq = requestsMap.get(epp.id);
                                  handleProcesarDevolucion(fullReq);
                                }}
                                title="Procesar Devolución"
                                className="border-2"
                              >
                                <FaUndo />
                              </Button>
                            )}
                            {(epp.status === "pending" || epp.status === "reserved") && (
                              <Badge bg="light" text="warning" className="border border-warning">
                                Pendiente Firma
                              </Badge>
                            )}
                            {epp.status === "returned" && (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{`Motivo: ${historialFiltrado.find(h => h.id === epp.id)?.motivo_devolucion || "No especificado"}`}</Tooltip>}
                              >
                                <Badge bg="light" text="danger" className="border border-danger">
                                  Dada de Baja
                                </Badge>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal de Asignación */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered scrollable>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center text-dark">
            <div
              className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(249, 115, 22, 0.1)", color: "#f97316" }}
            >
              <FaClipboardCheck size={20} />
            </div>
            <div>
              Entrega de Activos a Colaborador
              <p className="mb-0 text-muted small fw-normal">Seleccione los artículos (EPP, Equipos, Herramientas) y confirme</p>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-light">
          <Row className="g-0">
            <Col lg={4} className="border-end bg-white" style={{ minHeight: "75vh" }}>
              <div className="p-4 h-100 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0 text-dark">
                    <span className="badge bg-orange me-2" style={{ backgroundColor: "#f97316" }}>
                      {eppsSeleccionados.length}
                    </span>
                    Mi Selección (Carrito)
                  </h5>
                  {eppsSeleccionados.length > 0 && (
                    <Button
                      variant="link"
                      className="text-danger p-0 text-decoration-none small"
                      onClick={() => setEppsSeleccionados([])}
                    >
                      Vaciar
                    </Button>
                  )}
                </div>

                <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: "55vh" }}>
                  {eppsSeleccionados.length === 0 ? (
                    <div className="text-center py-5 mt-4 text-muted border border-dashed rounded-3 bg-light">
                      <div className="mb-3 fs-1 opacity-25">🛒</div>
                      <p className="small mb-0">No hay artículos seleccionados aún.</p>
                      <p className="xx-small mt-1">Usa el botón (+) del catálogo para empezar.</p>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {eppsSeleccionados.map((item, index) => (
                        <Card key={index} className="shadow-sm border-0 border-start border-4 border-warning">
                          <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="fw-bold text-dark small">{item.nombre}</div>
                              <Button
                                variant="link"
                                className="text-muted p-0"
                                onClick={() => removerEpp(index)}
                              >
                                <FaTrash size={12} />
                              </Button>
                            </div>

                            <div className="d-flex align-items-center gap-3 mb-3">
                              <div className="input-group input-group-sm" style={{ width: "110px" }}>
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => actualizarCantidadDirecta(index, item.cantidadAsignada - 1)}
                                >
                                  <FaMinus />
                                </Button>
                                <Form.Control
                                  className="text-center fw-bold"
                                  value={item.cantidadAsignada}
                                  readOnly
                                />
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => actualizarCantidadDirecta(index, item.cantidadAsignada + 1)}
                                >
                                  <FaPlus />
                                </Button>
                              </div>
                              <div className="small text-muted">Stock: {item.stock}</div>
                            </div>

                            <Form.Control
                              as="textarea"
                              rows={1}
                              placeholder="Observaciones..."
                              className="bg-light border-0 small"
                              style={{ fontSize: "0.75rem" }}
                              value={item.observacionesEpp}
                              onChange={(e) => actualizarObsDirecta(index, e.target.value)}
                            />
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-top">
                  <Button
                    variant="warning"
                    className="w-100 py-3 text-white fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2"
                    style={{ borderRadius: "12px", background: "#f97316" }}
                    onClick={handleAsignarEpp}
                    disabled={eppsSeleccionados.length === 0 || loading}
                  >
                    {loading ? <Spinner size="sm" /> : <><FaClipboardCheck /> CONFIRMAR ENTREGA</>}
                  </Button>
                </div>
              </div>
            </Col>

            {/* Columna Derecha: Catálogo y Búsqueda (66%) */}
            <Col lg={8} className="p-4">
              <div className="d-flex flex-column h-100">
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold mb-2">Seleccionar Artículos por Categoría</Form.Label>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => {
                      setActiveTab(k);
                      setBusqueda(""); // Limpiar búsqueda al cambiar de pestaña
                    }}
                    className="mb-3 custom-assignment-tabs"
                    justify
                  >
                    <Tab eventKey="epps" title={<span><BiPackage className="me-1" /> EPPs</span>} />
                    <Tab eventKey="equipos" title={<span><BiDevices className="me-1" /> Equipos</span>} />
                    <Tab eventKey="herramientas" title={<span><BiWrench className="me-1" /> Herramientas</span>} />
                  </Tabs>

                  <div className="position-relative mb-3">
                    <FaSearch className="position-absolute" style={{ left: "15px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
                    <Form.Control
                      ref={searchInputRef}
                      type="text"
                      placeholder={`Buscar en ${activeTab === 'epps' ? 'EPPs' : activeTab === 'equipos' ? 'Equipos' : 'Herramientas'}...`}
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      className="ps-5 border-2"
                      style={{ borderRadius: "10px" }}
                    />
                  </div>
                </Form.Group>

                <div className="table-responsive bg-white rounded-3 shadow-sm flex-grow-1" style={{ maxHeight: "60vh" }}>
                  <Table hover size="sm" className="align-middle mb-0">
                    <thead className="sticky-top bg-white z-index-1 border-bottom shadow-sm">
                      <tr>
                        <th className="py-3 ps-4">Código</th>
                        <th className="py-3">Descripción del Artículo</th>
                        <th className="py-3">Stock</th>
                        <th className="py-3 text-end pe-4">Añadir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eppsFiltrados.length === 0 ? (
                        <tr><td colSpan="4" className="text-center py-5 text-muted">No se encontraron resultados.</td></tr>
                      ) : (
                        eppsFiltrados.map((epp) => {
                          const estaEnCarrito = eppsSeleccionados.some(s => s.codigo === epp.codigo);
                          return (
                            <tr key={epp.id} className={estaEnCarrito ? "bg-orange-light-opacity" : ""}>
                              <td className="ps-4"><code>{epp.codigo}</code></td>
                              <td>
                                <div className="fw-bold">{epp.nombre}</div>
                                <div className="small text-muted d-flex gap-2">
                                  <span>Marca: {epp.marca || "N/A"}</span>
                                  <span>• Talla: {epp.talla || "STD"}</span>
                                  {epp.periodoReposicion && (
                                    <span className="text-orange" style={{ color: "#f97316" }}>• Reposición: {epp.periodoReposicion}m</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Badge
                                  bg={epp.stock > 5 ? "light" : epp.stock > 0 ? "warning" : "danger"}
                                  className={epp.stock > 5 ? "text-dark" : "text-white"}
                                >
                                  {epp.stock} Uds
                                </Badge>
                              </td>
                              <td className="text-end pe-4">
                                <Button
                                  variant={estaEnCarrito ? "warning" : "outline-warning"}
                                  size="sm"
                                  className="rounded-circle p-0 d-inline-flex align-items-center justify-content-center shadow-sm"
                                  style={{ width: "32px", height: "32px", border: "2px solid" }}
                                  disabled={epp.stock <= 0}
                                  onClick={() => agregarEpp(epp)}
                                >
                                  {estaEnCarrito ? <FaCheck /> : <FaPlus />}
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default EntregaActivos;
